/**
 * Team shape geometry. The final set (maintainer, 2026-11-01;
 * docs/team-games/team-final-four.html): the circle, a square with rounded
 * corners, the V3 optical-size diamond and the H2 optical-size hexagon (flat
 * top and bottom). Which team gets which shape is `teamShape` in
 * `@warcarp/api-types`.
 *
 * The mockup draws every shape in a 0–100 box centred on (50, 50), where the
 * circle has radius 48. The diamond and hexagon deliberately overshoot that
 * box a little so they look the same size as the circle. Here the paths are
 * centre-relative and scaled so the circle is `size` wide; a ring is the same
 * shape drawn larger around the same centre.
 */
import type { TeamId, TeamShapeName } from '@warcarp/api-types'

/** The design box: the circle's diameter in the mockup's units. */
const UNIT = 96

/**
 * How far each outline reaches from the centre along the axes, in design
 * units (the curves' apexes, not their control points). The diamond's
 * rounded tips peak at 51.75; the hexagon's side points at 52.5 and its flat
 * top at 46.8.
 */
const EXTENT: Record<TeamShapeName, { top: number; side: number }> = {
  circle: { top: 48, side: 48 },
  square: { top: 46, side: 46 },
  diamond: { top: 51.75, side: 51.75 },
  hexagon: { top: 46.8, side: 52.5 },
}

/**
 * Perpendicular distance from the centre to each shape's straight edges, in
 * design units. Scaling a polygon about its centre moves its edges by this
 * fraction of the radius, so a ring of even width needs the scale corrected
 * by 48 / apothem (the diamond's edges sit at 56/√2 ≈ 39.6).
 */
const APOTHEM: Record<TeamShapeName, number> = {
  circle: 48,
  square: 46,
  diamond: 56 / Math.SQRT2,
  hexagon: 46.8,
}

/**
 * The shape size whose outline sits `offset` px outside a shape `size` wide,
 * measured square to its edges, so rings and glows look equally thick on all
 * four shapes. For the circle this is just size + 2 × offset.
 */
export function teamShapeOutset(shape: TeamShapeName, size: number, offset: number): number {
  return size + (2 * offset * 48) / APOTHEM[shape]
}

/** How far a shape `size` wide reaches from its centre at its farthest tip. */
export function teamShapeReach(shape: TeamShapeName, size: number): number {
  const { top, side } = EXTENT[shape]
  return (Math.max(top, side) * size) / UNIT
}

/** The farthest any shape reaches from its centre along an axis. */
export const TEAM_SHAPE_MAX_EXTENT = 52.5

/** H2 optical-size hexagon, verbatim from the mockup (0–100 box). */
const HEXAGON = 'M101.0 44.8 Q104.0 50.0 101.0 55.2 L80.0 91.6 Q77.0 96.8 71.0 96.8 L29.0 96.8 Q23.0 96.8 20.0 91.6 L-1.0 55.2 Q-4.0 50.0 -1.0 44.8 L20.0 8.4 Q23.0 3.2 29.0 3.2 L71.0 3.2 Q77.0 3.2 80.0 8.4 Z'
/** V3 optical-size diamond, verbatim from the mockup (0–100 box). */
const DIAMOND = 'M41.5 2.5 Q50.0 -6.0 58.5 2.5 L97.5 41.5 Q106.0 50.0 97.5 58.5 L58.5 97.5 Q50.0 106.0 41.5 97.5 L2.5 58.5 Q-6.0 50.0 2.5 41.5 Z'

const n = (value: number) => Math.round(value * 100) / 100

/** Re-centre and scale a path whose commands all take x y pairs (M, L, Q). */
function placePairs(d: string, x: (u: number) => number, y: (u: number) => number): string {
  return d.replace(/([MLQ])([^MLQZ]*)/g, (_, cmd: string, args: string) => {
    const nums = args.trim().split(/\s+/).map(Number)
    const out: number[] = []
    for (let i = 0; i < nums.length; i += 2) out.push(x(nums[i]! - 50), y(nums[i + 1]! - 50))
    return `${cmd}${out.join(' ')} `
  }).trim()
}

/** SVG path data for `shape`, scaled so the circle would be `size` wide, centred on (cx, cy). */
export function teamShapePath(shape: TeamShapeName, cx: number, cy: number, size: number): string {
  const s = size / UNIT
  const x = (u: number) => n(cx + u * s)
  const y = (u: number) => n(cy + u * s)
  switch (shape) {
    case 'square': {
      // M14 4 H86 Q96 4 96 14 V86 Q96 96 86 96 H14 Q4 96 4 86 V14 Q4 4 14 4 Z
      const h = 46
      const r = 10
      return `M${x(-h + r)} ${y(-h)} H${x(h - r)} Q${x(h)} ${y(-h)} ${x(h)} ${y(-h + r)} V${y(h - r)} Q${x(h)} ${y(h)} ${x(h - r)} ${y(h)} H${x(-h + r)} Q${x(-h)} ${y(h)} ${x(-h)} ${y(h - r)} V${y(-h + r)} Q${x(-h)} ${y(-h)} ${x(-h + r)} ${y(-h)} Z`
    }
    case 'hexagon':
      return placePairs(HEXAGON, x, y)
    case 'diamond':
      return placePairs(DIAMOND, x, y)
    case 'circle':
    default:
      // Two half arcs: a single near-closed arc renders unreliably on some engines.
      return `M${x(-48)} ${y(0)} A${n(48 * s)} ${n(48 * s)} 0 1 0 ${x(48)} ${y(0)} A${n(48 * s)} ${n(48 * s)} 0 1 0 ${x(-48)} ${y(0)} Z`
  }
}

/** The shape's top edge, as a distance above the centre, for a circle `size` wide. */
export function teamShapeTop(shape: TeamShapeName, size: number): number {
  return (EXTENT[shape].top * size) / UNIT
}

/**
 * Neutral team tints for the drawer's share bar (team-shapes.html §3). Not
 * player colours: a segment stands for the whole team. Light enough that the
 * dark ink label reads on them in both themes.
 */
export const TEAM_TINTS: Record<TeamId, string> = {
  A: '#e8c860',
  B: '#9fb0c8',
  C: '#d8a0a0',
  D: '#a8c89a',
}

/** Ink for labels drawn on a team tint, in both themes. */
export const TEAM_TINT_INK = '#1d1c12'
